import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  DialogActions,
  FormLabel,
  Grid,
  RadioGroup,
  Snackbar,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { FormControl } from '../../../../../ui/components/pure/form-control/form-control.component';
import { TextField } from '../../../../../ui/components/pure/text-field/text-field.component';
import { AlertColor, default as Alert } from '@mui/material/Alert';
import { Modal } from '../../../../../ui/components/pure/modal/modal.component';
import { useMutationUpdateRole } from './edit-role.queries';
import { useQueryGetProject } from '../../../../../core/queries/projects.queries';
import {
  EditAccountDto,
  EditProjectMembershipDto,
  ProjectTeamMemberDto
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { useMutationUpdateAccount, useQueryGetProjectTeamMember } from '../../../team.queries';
import { TwoPaneModalContent } from '../../../../../ui/components/pure/modal-content/two-pane-modal-content.component';
import { mobilePhoneValidator } from '../../../../../ui/util/mobile-phone.validator';
import { useMobilePhoneFormatter } from '../../../../../ui/formatters/mobile-phone.formatter';
import { TextButton } from '../../../../../ui/components/pure/text-button/text-button.component';
import { AccessOption } from '../../../components/access-option.component';
import { useAccessLevelDescriptions } from '../../../hooks/use-access-level-descriptions.hook';
import { accessLevelsForProjectType } from '../../logic/team-member-details.constants';
import { useInviteMemberModalStyles } from '../../../list/modals/invite-team-member-modal/invite-team-member-modal.styles';
import { useHasPermissions } from '../../../../../core/permissions/has-permissions.hook';
import { Permissions } from '../../../../../core/permissions/permissions.typings';
import { AlertTitle } from '@mui/material';
import { parseErrorMessage } from '../../../../../core/api/api.context';

export interface EditRoleModalProps {
  open: boolean;
  projectID: any;
  userID: string;
  onClose: () => void;
}

export const EditRoleModal = ({ open, projectID, userID, onClose }: EditRoleModalProps) => {
  const classes = useInviteMemberModalStyles();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  const mobilePhoneFormatter = useMobilePhoneFormatter(projectID);

  const user = useQueryGetProjectTeamMember(projectID, userID).data;
  const project = useQueryGetProject(projectID).data;
  // The roles follow the project, so nobody can be edited into one it has no use for.
  const accessLevels = accessLevelsForProjectType(project?.projectType);
  const descriptions = useAccessLevelDescriptions(projectID);

  const { hasAllPermissions: canUpdateAccountDetails } = useHasPermissions([
    {
      action: Permissions.accounts.update,
      projectID
    }
  ]);

  const { mutateAsync: updateRoleMutation } = useMutationUpdateRole(projectID, userID);
  const { mutateAsync: updateAccountMutation } = useMutationUpdateAccount(projectID, userID);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const {
    control,
    handleSubmit: rfSubmit,
    reset
  } = useForm({
    mode: 'onTouched',
    defaultValues: {
      role: user?.role || '',
      accessLevel: user?.accessLevel || '',
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      mobile: user?.mobile || '',
      externalID: user?.externalID || ''
    }
  });

  const [snackState, setSnackState] = useState<{ msg: string; severity: AlertColor }>();

  const showSnack = (msg: string, severity: AlertColor) => setSnackState({ msg, severity });
  const hideSnack = () => setSnackState(undefined);

  const handleClose = () => {
    reset();
    onClose();
  };

  async function updateMembership(data: EditProjectMembershipDto) {
    await updateRoleMutation({
      role: data.role,
      accessLevel: data.accessLevel
    });
  }

  async function updateAccount(data: EditAccountDto) {
    await updateAccountMutation({
      firstName: data.firstName,
      lastName: data.lastName,
      mobile: data.mobile,
      email: data.email,
      externalID: data.externalID
    });
  }

  async function submitChanges(data: any) {
    const changedMembershipFields = getChangedMembershipFields(user, data);
    const changedAccountFields = getChangedAccountFields(user, data);
    const shouldUpdateAccount = Object.keys(changedAccountFields).length > 0;
    const shouldUpdateMembership = Object.keys(changedMembershipFields).length > 0;
    // Depending on the changes being made, the order of mutations might be important.
    // We can't know which order will work, so we try both.
    if (shouldUpdateAccount && shouldUpdateMembership) {
      try {
        await updateAccount(changedAccountFields);
        await updateMembership(changedMembershipFields);
      } catch {
        await updateMembership(changedMembershipFields);
        await updateAccount(changedAccountFields);
      }
    } else if (shouldUpdateAccount) {
      await updateAccount(changedAccountFields);
    } else if (shouldUpdateMembership) {
      await updateMembership(changedMembershipFields);
    }
  }

  const handleSubmit = async (data: any) => {
    setLoading(true);
    try {
      await submitChanges(data);
      showSnack('Role updated successfully', 'success');
      handleClose();
    } catch (error: any) {
      let message = 'Failed to update role, please try again';
      if (typeof error === 'object' && error && 'message' in error) {
        message = parseErrorMessage(error.message);
      }
      setLoading(false);
      setError(message);
    }
  };

  const completeName = [user?.firstName, user?.lastName].join(' ');

  return (
    <>
      {snackState && (
        <Snackbar
          anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
          open={Boolean(snackState)}
          autoHideDuration={5000}
          onClose={hideSnack}
        >
          <Alert onClose={hideSnack} severity={snackState.severity}>
            <Typography>{snackState.msg}</Typography>
          </Alert>
        </Snackbar>
      )}
      <Modal
        open={open}
        title={`Edit ${completeName}`}
        loading={(!user && !project) || loading}
        maxWidth="md"
        onClose={handleClose}
      >
        <TwoPaneModalContent
          leftDesktopWidth={6}
          rigthDesktopWidth={6}
          flexibleWidthPane={'right'}
          leftContent={() => (
            <Grid container sx={{ flexDirection: 'column' }} style={{ padding: '0px 16px', width: '100%', margin: 0 }}>
              {error ? (
                <Alert variant="outlined" severity="error" style={{ marginBottom: '16px' }}>
                  <AlertTitle>Something went wrong</AlertTitle>
                  {error}
                </Alert>
              ) : null}
              <Grid container spacing={isXs ? 0 : 6}>
                <Grid
                  style={{ marginBottom: '10px' }}
                  size={{
                    xs: 12,
                    sm: 6
                  }}
                >
                  <FormControl
                    control={control}
                    name="firstName"
                    required
                    ComponentProps={{
                      textFieldVariant: 'floating' as const,
                      placeholder: 'First name',
                      style: { width: '100%' },
                      disabled: !canUpdateAccountDetails
                    }}
                    Component={TextField}
                  />
                </Grid>
                <Grid
                  style={{ marginBottom: '10px' }}
                  size={{
                    xs: 12,
                    sm: 6
                  }}
                >
                  <FormControl
                    control={control}
                    name="lastName"
                    Component={TextField}
                    ComponentProps={{
                      textFieldVariant: 'floating' as const,
                      placeholder: 'Last name',
                      style: { width: '100%' },
                      disabled: !canUpdateAccountDetails
                    }}
                    required
                  />
                </Grid>
              </Grid>
              <Grid style={{ marginBottom: '10px' }} size={12}>
                <FormControl
                  control={control}
                  name="role"
                  required
                  Component={TextField}
                  ComponentProps={{
                    textFieldVariant: 'floating' as const,
                    placeholder: 'Role',
                    style: { width: '100%' }
                  }}
                />
              </Grid>
              <Grid style={{ marginBottom: '10px' }} size={12}>
                <FormControl
                  control={control}
                  name="email"
                  Component={TextField}
                  ComponentProps={{
                    type: 'email',
                    textFieldVariant: 'floating' as const,
                    placeholder: 'Email address',
                    style: { width: '100%' },
                    disabled: !!user?.email
                  }}
                />
              </Grid>
              <Grid style={{ marginBottom: '10px' }} size={12}>
                <FormControl
                  control={control}
                  name="mobile"
                  rules={{
                    validate: (mobile: string | null) => !mobile || mobilePhoneValidator(mobile)
                  }}
                  setValueAs={(mobile) => {
                    // If the mobile number is falsy or empty assume that we want to unset the value
                    if (!mobile || mobile === '') {
                      return '';
                    }
                    return mobilePhoneFormatter(mobile);
                  }}
                  Component={TextField}
                  ComponentProps={{
                    type: 'tel',
                    textFieldVariant: 'floating' as const,
                    placeholder: 'Mobile number',
                    style: { width: '100%' },
                    disabled: !canUpdateAccountDetails
                  }}
                />
              </Grid>
              {/* External ID is typed as string intentionally — kiosks currently accept numbers only but the field is designed to support arbitrary string identifiers */}
              <Grid style={{ marginBottom: '10px' }} size={12}>
                <FormControl
                  control={control}
                  name="externalID"
                  Component={TextField}
                  ComponentProps={{
                    textFieldVariant: 'floating' as const,
                    placeholder: 'External ID',
                    style: { width: '100%' },
                    disabled: !canUpdateAccountDetails
                  }}
                />
              </Grid>
            </Grid>
          )}
          rightContent={() => (
            <Grid container sx={{ flexDirection: 'column' }} style={{ padding: '16px' }}>
              <FormLabel component="legend" required style={{ marginBottom: '10px' }}>
                Access Level
              </FormLabel>
              <Controller
                control={control}
                name="accessLevel"
                rules={{ required: true }}
                render={({ field }) => (
                  <RadioGroup {...field} className={classes.AccessLevelRadioGroup}>
                    {accessLevels.map((level) => (
                      <AccessOption key={level} level={level} description={descriptions[level]} classes={classes} />
                    ))}
                  </RadioGroup>
                )}
              />
            </Grid>
          )}
        />
        <DialogActions style={{ borderTop: '1px solid #EBEDF2', padding: '16px 24px' }}>
          <TextButton color="secondary" variant="outlined" onClick={handleClose}>
            Cancel
          </TextButton>
          <TextButton color="primary" variant="outlined" onClick={rfSubmit(handleSubmit)}>
            Save
          </TextButton>
        </DialogActions>
      </Modal>
    </>
  );
};

function getChangedFields<T extends Partial<Record<keyof ProjectTeamMemberDto, string | null>>>(
  keys: (keyof ProjectTeamMemberDto)[],
  user: ProjectTeamMemberDto | undefined,
  data: T
) {
  return keys.reduce((acc, key) => {
    // Only include the field if it has changed
    if (data[key] === '' && user?.[key] === undefined) {
      return acc;
    }
    if (user?.[key] !== data[key]) {
      // If the user previously had a value and the new value is empty, unset the value
      acc[key] = data[key] === '' && user?.[key] !== undefined ? null : data[key];
    }
    return acc;
  }, {} as T);
}

function getChangedMembershipFields(
  user: ProjectTeamMemberDto | undefined,
  data: EditProjectMembershipDto
): EditProjectMembershipDto {
  return getChangedFields(['role', 'accessLevel'], user, data);
}

function getChangedAccountFields(user: ProjectTeamMemberDto | undefined, data: EditAccountDto): EditAccountDto {
  return getChangedFields(['firstName', 'lastName', 'mobile', 'email', 'externalID'], user, data);
}
